import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';
import { apiRequest } from '../utils/api';
import { subscribeToForegroundPush } from '../utils/firebaseNotifications';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const [unseenRewards, setUnseenRewards] = useState(0);
  const [incomingFriendRequests, setIncomingFriendRequests] = useState(0);
  const [suppressRewardNotifications, setSuppressRewardNotifications] = useState(false);
  const [foregroundPush, setForegroundPush] = useState(null);

  useEffect(() => subscribeToForegroundPush(setForegroundPush), []);

  const refreshUnseenRewards = useCallback(async ({ force = false } = {}) => {
    if (suppressRewardNotifications && !force) return null;
    const data = await apiRequest('/api/images', { skipUnauthorizedRedirect: true });
    const unseenCount = data.unseen_count || 0;
    const pendingRewardCount = (data.reward_jobs || []).filter((job) => (
      job.status === 'pending' || job.status === 'processing'
    )).length;
    const blockedRewardCount = (data.blocked_rewards || []).length;
    setUnseenRewards(
      unseenCount + pendingRewardCount + blockedRewardCount,
    );
    return data;
  }, [suppressRewardNotifications]);

  const refreshFriendRequests = useCallback(async () => {
    const data = await apiRequest('/api/friend-requests', { skipUnauthorizedRedirect: true });
    const incomingCount = (data.incoming || []).length;
    setIncomingFriendRequests(incomingCount);
    return data;
  }, []);

  const refreshNotifications = useCallback(async () => {
    const [rewardsResult, friendsResult] = await Promise.allSettled([
      refreshUnseenRewards(),
      refreshFriendRequests(),
    ]);

    if (rewardsResult.status === 'rejected') {
      console.error('Failed to fetch unseen rewards count:', rewardsResult.reason);
    }
    if (friendsResult.status === 'rejected') {
      console.error('Failed to fetch friend request count:', friendsResult.reason);
    }
  }, [refreshFriendRequests, refreshUnseenRewards]);

  const value = useMemo(() => ({
    incomingFriendRequests,
    refreshFriendRequests,
    refreshNotifications,
    refreshUnseenRewards,
    setIncomingFriendRequests,
    setSuppressRewardNotifications,
    setUnseenRewards,
    suppressRewardNotifications,
    unseenRewards,
  }), [
    incomingFriendRequests,
    refreshFriendRequests,
    refreshNotifications,
    refreshUnseenRewards,
    suppressRewardNotifications,
    unseenRewards,
  ]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Snackbar
        open={Boolean(foregroundPush)}
        autoHideDuration={9000}
        onClose={() => setForegroundPush(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setForegroundPush(null)}
          onClick={() => {
            if (foregroundPush?.url) window.location.assign(foregroundPush.url);
          }}
          sx={{ cursor: foregroundPush?.url ? 'pointer' : 'default', width: '100%' }}
        >
          <strong>{foregroundPush?.title}</strong>
          {foregroundPush?.body ? ` — ${foregroundPush.body}` : ''}
        </Alert>
      </Snackbar>
    </NotificationsContext.Provider>
  );
};
